// CRM Companies — God's-eye table, filter engine, contact linkage as the spine.
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../../config';
import CrmTable from '../../components/CrmTable';
import { COMPANY_COLUMNS, ALL_COMPANY_COLUMNS } from '../../components/crmColumns';
import FilterRail from '../../components/FilterRail';
import { COMPANY_SCHEMA } from '../../components/filterSchema';
import SectorPicker from '../../components/SectorPicker';
import CountryPicker from '../../components/CountryPicker';
import BulkRunModal from '../../components/BulkRunModal';
import toast from 'react-hot-toast';

export default function CrmCompanies() {
  const { clientId, client } = useOutletContext();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => COMPANY_COLUMNS.map(c => c.key));
  const [draftKeys, setDraftKeys] = useState(() => COMPANY_COLUMNS.map(c => c.key));
  const [dragKey, setDragKey] = useState(null);

  const [showAddCompany, setShowAddCompany] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const emptyCompanyForm = { name: '', website: '', sector: '', size: '', location: '', linkedin_url: '' };
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);

  const [filterTree, setFilterTree] = useState(null);
  const [sfRows, setSfRows] = useState(null);
  const [sfTotal, setSfTotal] = useState(0);
  const [sfLoading, setSfLoading] = useState(false);
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const targets = {};
  const [distincts, setDistincts] = useState({});
  const [selectingAll, setSelectingAll] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (showColumns) setDraftKeys(visibleKeys); }, [showColumns]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterActive = !!(filterTree && Array.isArray(filterTree.and) && filterTree.and.length > 0);

  // Unfiltered load goes through the filter engine with an empty tree so the
  // row shape is identical to the filtered path (one God's-eye projection).
  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/filter/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), tree: null, sort, limit: 50000, offset: 0 }),
      });
      if (!r.ok) throw new Error(`Failed to load companies (${r.status})`);
      const data = await r.json();
      setCompanies(Array.isArray(data.rows) ? data.rows : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, getToken, sort]);
  useEffect(() => { load(); }, [load]);

  async function saveCompany() {
    if (!companyForm.name.trim() || savingCompany) return;
    setSavingCompany(true);
    try {
      const r = await fetch(`${API_BASE}/clients/${clientId}/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyForm),
      });
      if (!r.ok) throw new Error(`Failed to add company (${r.status})`);
      setShowAddCompany(false);
      setCompanyForm(emptyCompanyForm);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingCompany(false);
    }
  }

  const runServerFilter = useCallback(async (tree, pg, size) => {
    if (!tree || !Array.isArray(tree.and) || tree.and.length === 0) { setSfRows(null); setSfTotal(0); return; }
    setSfLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/filter/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), tree, sort, limit: size, offset: pg * size }),
      });
      const data = await res.json();
      if (res.ok) { setSfRows(data.rows || []); setSfTotal(data.total || 0); }
      else { setSfRows([]); setSfTotal(0); }
    } catch (e) { setSfRows([]); setSfTotal(0); }
    finally { setSfLoading(false); }
  }, [clientId, getToken, sort]);

  useEffect(() => { runServerFilter(filterTree, page, pageSize); }, [filterTree, page, pageSize, runServerFilter]);
  useEffect(() => { setPage(0); }, [query, pageSize, filterTree]);

  const loadSavedViews = useCallback(async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/saved-filters/companies?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setSavedViews(await r.json());
    } catch (_) {}
  }, [clientId, getToken]);

  const loadDistincts = useCallback(async () => {
    const fields = (COMPANY_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown);
    const uniq = [...new Set(fields)];
    const out = {};
    await Promise.all(uniq.map(async df => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/filter/companies/distinct/${encodeURIComponent(df)}?client_id=${parseInt(clientId)}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) out[df] = await r.json();
      } catch (_) {}
    }));
    setDistincts(out);
  }, [clientId, getToken]);

  useEffect(() => { if (clientId) { loadSavedViews(); loadDistincts(); } }, [clientId, loadSavedViews, loadDistincts]);

  async function saveCurrentView(name, shared) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), name, tree: filterTree, shared: !!shared }),
      });
      loadSavedViews();
    } catch (_) {}
  }
  async function deleteView(viewId) {
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/saved-filters/${viewId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (activeViewId === viewId) { setActiveViewId(null); setFilterTree(null); }
      loadSavedViews();
    } catch (_) {}
  }

  const q = query.trim().toLowerCase();
  const searched = q
    ? companies.filter(c => [c.name, c.sector, c.location, c.website].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
    : companies;

  const SORT_ROWKEY = { name: 'name', sector: 'sector', size: 'size', location: 'location', fit_score: 'score', status: 'status', id: 'id', created_at: 'created_at', updated_at: 'updated_at', contact_count: 'contact_count', swept_at: 'last_swept_at' };
  const sortKey = SORT_ROWKEY[sort.field] || 'created_at';
  const localFiltered = [...searched].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (sortKey === 'score' || sortKey === 'id' || sortKey === 'contact_count') { av = Number(av); bv = Number(bv); return sort.dir === 'asc' ? av - bv : bv - av; }
    if (sortKey === 'created_at' || sortKey === 'updated_at' || sortKey === 'last_swept_at') {
      av = new Date(av).getTime(); bv = new Date(bv).getTime();
      return sort.dir === 'asc' ? av - bv : bv - av;
    }
    av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
    return sort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const onSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
    setPage(0);
  };

  let totalCount, pageRows, totalPages, safePage;
  if (filterActive) {
    totalCount = sfTotal;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    safePage = Math.min(page, totalPages - 1);
    pageRows = sfRows || [];
  } else {
    totalCount = localFiltered.length;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    safePage = Math.min(page, totalPages - 1);
    pageRows = localFiltered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  }

  const toggle = (rowId) => setSelectedIds(prev => { const n = new Set(prev); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  const toggleAll = () => setSelectedIds(prev => {
    const on = pageRows.length > 0 && pageRows.every(r => prev.has(r.id));
    if (on) return new Set();
    return new Set(pageRows.map(r => r.id));
  });

  const refreshList = async () => {
    await load();
    if (filterActive) await runServerFilter(filterTree, page, pageSize);
  };

  const selectAllMatching = async () => {
    if (!filterActive) { setSelectedIds(new Set(localFiltered.map(r => r.id))); return; }
    setSelectingAll(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/bulk/resolve-ids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entity: 'companies', client_id: parseInt(clientId), filter: filterTree }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not select all');
      setSelectedIds(new Set((data.ids || []).map(Number)));
    } catch (e) { toast.error(e.message); }
    setSelectingAll(false);
  };

  const buildScanItems = () => {
    const byId = new Map([...companies, ...(sfRows || [])].map(c => [c.id, c]));
    const now = Date.now();
    return Array.from(selectedIds).map(cid => {
      const c = byId.get(cid);
      const last = c && c.last_swept_at ? new Date(c.last_swept_at) : null;
      const next = c && c.next_sweep_due ? new Date(c.next_sweep_due) : null;
      const due = !last || !next || next.getTime() <= now;
      return {
        id: cid,
        name: c ? c.name : `Company ${cid}`,
        sub: c && c.location ? c.location : '',
        reason: last ? `scanned ${last.toLocaleDateString('en-GB')}${due ? ', due again' : ''}` : 'never scanned',
        due,
      };
    });
  };

  const runScanOne = async (cid) => {
    try {
      const res = await fetch(`${API_BASE}/companies/${cid}/detect-signals`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.status === 402) return { creditsOut: true };
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, label: 'Failed' };
      const n = Array.isArray(data.signals) ? data.signals.length : 0;
      return { ok: true, label: n ? `${n} signal${n === 1 ? '' : 's'}` : 'No new signals' };
    } catch (e) { return { ok: false, label: 'Failed' }; }
  };

  const submitDelete = async () => {
    if (deleteText.trim() !== String(selectedIds.size)) { toast.error(`Type ${selectedIds.size} to confirm`); return; }
    setDeleting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/companies/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId), company_ids: Array.from(selectedIds) }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) throw new Error('Only workspace admins can delete companies');
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      const n = data.deleted || 0;
      const m = data.contacts_deleted || 0;
      toast.success(`Deleted ${n} compan${n === 1 ? 'y' : 'ies'} and ${m} contact${m === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setDeleteOpen(false);
      await refreshList();
    } catch (e) { toast.error(e.message); }
    setDeleting(false);
  };

  const ukDate = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB'); };
  const cellToText = (col, row) => {
    const v = row[col.key];
    switch (col.render) {
      case 'signals': { const arr = Array.isArray(v) ? v : []; return arr.filter(Boolean).join('; '); }
      case 'companyname': return row.name || '';
      case 'countpill': return v != null ? String(v) : '0';
      case 'sweep': {
        let s = row.sweep_status;
        if (!s) { if (!row.last_swept_at) s = 'never'; else if (row.next_sweep_due && new Date(row.next_sweep_due) > new Date()) s = 'fresh'; else s = 'due'; }
        return s === 'fresh' ? 'Up to date' : s === 'due' ? 'Needs scan' : 'Not scanned';
      }
      case 'bool': return v ? 'Yes' : 'No';
      case 'ukdate':
      case 'date': return ukDate(v);
      case 'link': return v || '';
      default: return (v == null) ? '' : String(v);
    }
  };
  const csvEscape = (s) => { const t = String(s == null ? '' : s); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
  const exportCsv = async () => {
    const cols = visibleKeys.map(k => ALL_COMPANY_COLUMNS.find(c => c.key === k)).filter(Boolean);
    let rows;
    if (filterActive) {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/filter/companies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ client_id: parseInt(clientId), tree: filterTree, sort, limit: 50000, offset: 0 }),
        });
        const data = await res.json();
        rows = res.ok ? (data.rows || []) : [];
      } catch (_) { rows = []; }
    } else { rows = localFiltered; }
    const header = cols.map(c => csvEscape(c.label)).join(',');
    const body = rows.map(r => cols.map(c => csvEscape(cellToText(c, r))).join(',')).join('\n');
    const csv = header + '\n' + body;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `companies-${(client && client.name ? client.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : clientId)}-${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="crmc-wrap">
      <div className="crmc-head">
        <div>
          <h1 className="crmc-title">Companies</h1>
          <p className="crmc-sub">{client ? client.name : 'Workspace'}</p>
        </div>
        <div className="crmc-head-right">
          <span className="crmc-count"><b>{totalCount}</b>{filterActive ? ' filtered' : ` of ${companies.length}`}</span>
        </div>
      </div>

      <div className="crmc-tabs">
        <button type="button" className={'crmc-tab' + (activeViewId === null ? ' crmc-tab--on' : '')}
          onClick={() => { setActiveViewId(null); setFilterTree(null); setPage(0); }}>All companies</button>
        {savedViews.map(v => (
          <button key={v.id} type="button" className={'crmc-tab' + (activeViewId === v.id ? ' crmc-tab--on' : '')}
            onClick={() => { setActiveViewId(v.id); setFilterTree(v.tree); setPage(0); }}>{v.name}</button>
        ))}
        <button type="button" className="crmc-tab crmc-tab--add" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="crmc-toolbar">
        <div className="crmc-tb-search">
          <span className="crmc-tb-searchicon">⌕</span>
          <input placeholder="Search companies" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="crmc-tb-actions">
          <button className="crmc-tb-btn crmc-tb-btn--primary" type="button" onClick={() => setShowAddCompany(true)}><span className="crmc-tb-ic">+</span>Add company</button>
          <button className={'crmc-tb-btn' + (showColumns ? ' crmc-tb-btn--on' : '')} type="button" onClick={() => setShowColumns(v => !v)}><span className="crmc-tb-ic">▦</span>Columns</button>
          <button className={'crmc-tb-btn' + (filterActive ? ' crmc-tb-btn--on' : '')} type="button" onClick={() => setShowFilters(v => !v)}>
            <span className="crmc-tb-ic">⛃</span>Filters{filterActive ? ` · ${filterTree.and.length}` : ''}
          </button>
          <button className="crmc-tb-btn" type="button" title="Click a column header to sort" onClick={() => setSort(prev => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))}><span className="crmc-tb-ic">↕</span>Sort {sort.dir === 'asc' ? '↑' : '↓'}</button>
          <button className="crmc-tb-btn" type="button" title="Export current view to CSV" onClick={exportCsv}><span className="crmc-tb-ic">↧</span>Export</button>
        </div>
      </div>

      {showColumns && (
        <div className="crmc-colpanel">
          <div className="crmc-colpanel-head">
            <span>Edit columns</span>
            <button type="button" className="crmc-colpanel-reset" onClick={() => setDraftKeys(COMPANY_COLUMNS.map(c => c.key))}>Reset to default</button>
          </div>
          <div className="crmc-colpanel-cols">
            <div className="crmc-colpanel-visible">
              <div className="crmc-colpanel-subhead">Visible — drag to reorder</div>
              {draftKeys.map(k => {
                const col = ALL_COMPANY_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                const locked = col.key === 'id' || col.key === 'name';
                return (
                  <div key={col.key}
                    className={'crmc-colrow' + (locked ? ' crmc-colrow--locked' : '') + (dragKey === col.key ? ' crmc-colrow--dragging' : '')}
                    draggable={!locked}
                    onDragStart={() => !locked && setDragKey(col.key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={e => {
                      e.preventDefault();
                      if (locked || !dragKey || dragKey === col.key) return;
                      setDraftKeys(prev => { const next = prev.filter(x => x !== dragKey); const idx = next.indexOf(col.key); next.splice(idx, 0, dragKey); return next; });
                    }}>
                    <span className="crmc-colgrip">{locked ? '🔒' : '⠿'}</span>
                    <span className="crmc-collabel">{col.label}</span>
                    {!locked && <button type="button" className="crmc-colremove" title="Hide column" onClick={() => setDraftKeys(prev => prev.filter(x => x !== col.key))}>×</button>}
                  </div>
                );
              })}
            </div>
            <div className="crmc-colpanel-hidden">
              <div className="crmc-colpanel-subhead">Hidden — click to add</div>
              <div className="crmc-colpanel-hiddengrid">
                {ALL_COMPANY_COLUMNS.filter(c => !draftKeys.includes(c.key)).map(col => (
                  <button type="button" key={col.key} className="crmc-coladd" onClick={() => setDraftKeys(prev => [...prev, col.key])}><span>+</span>{col.label}</button>
                ))}
                {ALL_COMPANY_COLUMNS.filter(c => !draftKeys.includes(c.key)).length === 0 && <div className="crmc-colpanel-allon">All columns visible</div>}
              </div>
            </div>
          </div>
          <div className="crmc-colpanel-foot">
            <button type="button" className="crmc-colbtn" onClick={() => setShowColumns(false)}>Cancel</button>
            <button type="button" className="crmc-colbtn crmc-colbtn--apply" onClick={() => { setVisibleKeys(draftKeys); setShowColumns(false); }}>Apply</button>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="crmc-bulk">
          <span className="crmc-bulk-n">{selectedIds.size} selected</span>
          {selectedIds.size < totalCount && (
            <button type="button" className="crmc-bulk-link" onClick={selectAllMatching} disabled={selectingAll}>
              {selectingAll ? 'Selecting\u2026' : `Select all ${totalCount}`}
            </button>
          )}
          <button type="button" className="crmc-bulk-link crmc-bulk-link--muted" onClick={() => setSelectedIds(new Set())}>Clear</button>
          <div className="crmc-bulk-right">
            <button type="button" className="crmc-bulk-btn" onClick={() => setScanOpen(true)}>Scan {selectedIds.size} for signals</button>
            <button type="button" className="crmc-bulk-btn crmc-bulk-btn--danger" onClick={() => { setDeleteText(''); setDeleteOpen(true); }}>Delete {selectedIds.size}</button>
          </div>
        </div>
      )}

      {scanOpen && (
        <BulkRunModal
          title="Scan companies for signals"
          verb="Scan"
          skipNote="skipped, already up to date"
          forceLabel="Rescan all selected"
          items={buildScanItems()}
          concurrency={1}
          gapMs={1000}
          runOne={runScanOne}
          onClose={(ran) => { setScanOpen(false); if (ran) refreshList(); }}
        />
      )}

      {deleteOpen && (
        <div className="crmsq3-ov" onMouseDown={e => { if (e.target === e.currentTarget && !deleting) setDeleteOpen(false); }}>
          <div className="crmsq3-modal">
            <div className="crmsq3-modal-h">
              <h3>{`Delete ${selectedIds.size} compan${selectedIds.size === 1 ? 'y' : 'ies'}?`}</h3>
              <button type="button" className="crmsq3-modal-x" aria-label="Close" onClick={() => { if (!deleting) setDeleteOpen(false); }}>{'\u00D7'}</button>
            </div>
            <div className="crmsq3-modal-b">
              <p className="crmsq3-modal-s">Their contacts, sequences, scores and signals go with them. This can't be undone.</p>
              <span className="crmsq3-modal-lbl">Type {selectedIds.size} to confirm</span>
              <input className="crmsq3-modal-inp" value={deleteText} onChange={e => setDeleteText(e.target.value)} autoFocus />
            </div>
            <div className="crmsq3-modal-f">
              <button type="button" className="crmsq3-btn crmsq3-btn--ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</button>
              <button type="button" className="crmsq3-btn crmsq3-btn--danger" onClick={submitDelete} disabled={deleting || deleteText.trim() !== String(selectedIds.size)}>{deleting ? 'Deleting\u2026' : `Delete ${selectedIds.size}`}</button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="crmc-error">{error}</div>}

      <div className="crmc-body">
        {showFilters && (
          <div className="crmc-filterpanel">
            <FilterRail
              schema={COMPANY_SCHEMA}
              value={filterTree}
              onChange={(t) => { setFilterTree(t); setPage(0); setActiveViewId(null); }}
              matchCount={filterActive ? sfTotal : undefined}
              loading={sfLoading}
              savedViews={savedViews}
              onSaveView={saveCurrentView}
              onApplyView={(v) => { setFilterTree(v.tree); setPage(0); setActiveViewId(v.id); }}
              onDeleteView={deleteView}
              targets={targets}
              distincts={distincts}
            />
          </div>
        )}
        <div className="crmc-tablecol">
          <CrmTable
            columns={visibleKeys.map(k => ALL_COMPANY_COLUMNS.find(c => c.key === k)).filter(Boolean)}
            rows={pageRows}
            loading={filterActive ? sfLoading : loading}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
            sort={sort}
            onSort={onSort}
            onContactClick={(row) => navigate(`/clients/${clientId}/crm/companies/${row.id}`)}
          />
        </div>
      </div>

      <div className="crmc-footer">
        <div className="crmc-pagesize">
          <span>Rows per page</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="crmc-pager">
          <button disabled={safePage <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>
          <span className="crmc-pageno">{safePage + 1} of {totalPages}</span>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next</button>
        </div>
        <div className="crmc-rangeinfo">
          {totalCount === 0 ? '0' : `${safePage * pageSize + 1}\u2013${Math.min(totalCount, safePage * pageSize + pageRows.length)}`} of {totalCount}
        </div>
      </div>

      {showAddCompany && (
        <div className="crmc-modal-overlay" onClick={() => setShowAddCompany(false)}>
          <div className="crmc-modal" onClick={e => e.stopPropagation()}>
            <div className="crmc-modal-head">
              <h3>Add company</h3>
              <button type="button" className="crmc-modal-x" onClick={() => setShowAddCompany(false)}>×</button>
            </div>
            <div className="crmc-modal-body">
              <div className="crmc-modal-grid">
                <div className="crmc-field"><label>Company name *</label><input value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} placeholder="e.g. Acme Ltd" /></div>
                <div className="crmc-field"><label>Website</label><input value={companyForm.website} onChange={e => setCompanyForm({ ...companyForm, website: e.target.value })} placeholder="e.g. acme.co.uk" /></div>
                <div className="crmc-field crmc-field--full"><label>Sector</label><SectorPicker value={companyForm.sector} onChange={vals => setCompanyForm({ ...companyForm, sector: JSON.stringify(vals) })} /></div>
                <div className="crmc-field"><label>Size</label><input value={companyForm.size} onChange={e => setCompanyForm({ ...companyForm, size: e.target.value })} placeholder="e.g. 10-50" /></div>
                <div className="crmc-field"><label>Country</label><CountryPicker value={companyForm.location} onChange={val => setCompanyForm({ ...companyForm, location: val })} /></div>
                <div className="crmc-field crmc-field--full"><label>LinkedIn URL</label><input value={companyForm.linkedin_url} onChange={e => setCompanyForm({ ...companyForm, linkedin_url: e.target.value })} placeholder="linkedin.com/company/acme" /></div>
              </div>
            </div>
            <div className="crmc-modal-foot">
              <button type="button" className="crmc-modal-save" onClick={saveCompany} disabled={savingCompany || !companyForm.name.trim()}>{savingCompany ? 'Saving…' : 'Save company'}</button>
              <button type="button" className="crmc-modal-cancel" onClick={() => setShowAddCompany(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
